/**
 * The deck screen's «Тексты» on desktop (handoff §19.6, artboard 27): the list
 * column and, beside it, a reading pane for the selected text.
 *
 * It is laid out by the card panels' own geometry (`deckPaneLayout`): the same
 * shell, the same 412 px list floor and pane width, the same floor inset, so the
 * deck screen does not change shape when the segment does. The list scrolls in the
 * column and the pane scrolls as its own box, as the card panels' do (ADR-067
 * Decision 3). Practice is not here yet: the pane's «Тренировать текст» and its
 * note come with the player.
 *
 * Below the two-pane threshold (`narrow`) there is no pane: a row opens the text.
 */
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLocale } from '../../i18n/context'
import { useDeckTexts, useText } from '../../hooks/useTexts'
import { DECK_FLOOR_INSET } from '../../utils/deckPaneViewport'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'
import type { TextDetail, TextListItem } from '../../types'
import { leftColumnStyle, leftPanelStyle, panelsShellStyle, rightPanelStyle } from '../deckPaneLayout'
import DeckTextsEmpty from './DeckTextsEmpty'
import GappedText from './GappedText'
import { OutlinePill, SecondaryPill } from './ui'
import { practiceStatus } from '../../utils/textPractice'

/** A text has three levels of its own (ADR-116 Decision 4). */
const TEXT_LEVEL_MAX = 3

// The pane's width: its reading width, or what the 412 px list floor leaves.
const PANE_WIDTH = 'min(var(--deck-pane-width-reading), 100% - var(--deck-list-min-width) - var(--deck-pane-gutter))'

const ROW_CSS = `
.dt-drow{cursor:pointer;transition:border-color .12s,background .12s}
.dt-drow:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
@media (hover:hover){.dt-drow:not([aria-pressed="true"]):hover{background:var(--row-plane-hover)!important;border-color:var(--row-border-hover)!important}}
`

interface Props {
  deck: { id: string; title: string }
  /** The list column's head: the segment switch and «Добавить текст», built by the page. */
  head: React.ReactNode
  /** Below the two-pane threshold: no pane, a row opens the text. */
  narrow: boolean
  onAdd: () => void
}

export default function DeckTextsPanels({ deck, head, narrow, onAdd }: Props) {
  const { tr } = useLocale()
  const t = tr.deckTexts
  const navigate = useNavigate()
  const location = useLocation()
  // The desktop rows show no word count, so the list is asked for without it.
  const { data, isLoading, isError, refetch } = useDeckTexts(deck.id, false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const items = data?.items ?? []
  // A text deleted or unlinked elsewhere leaves nothing selected.
  const selected = items.find((i) => i.id === selectedId) ?? null
  const openText = (id: string) =>
    navigate(`/texts/${id}`, { state: { from: location.pathname + location.search, fromLabel: deck.title } })
  // The player resumes a run in progress by itself; leaving it comes back to this deck's pane.
  const practiceText = (id: string) =>
    navigate(`/texts/${id}/practice`, { state: { from: location.pathname + location.search, fromLabel: deck.title } })

  let list: React.ReactNode
  if (isLoading) {
    list = (
      <div aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[0, 1].map((i) => <div key={i} style={{ height: 78, borderRadius: 8, background: 'var(--bg-secondary)', opacity: 0.6 }} />)}
      </div>
    )
  } else if (isError || !data) {
    list = (
      <div role="alert" style={{ textAlign: 'center', padding: '32px 8px', fontFamily: 'var(--font-ui)' }}>
        <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t.loadFailed}</p>
        <SecondaryPill onClick={() => refetch()}>{t.retry}</SecondaryPill>
      </div>
    )
  } else if (items.length === 0) {
    list = <DeckTextsEmpty onAdd={onAdd} />
  } else {
    list = (
      <ul role="list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((text) => (
          <li key={text.id}>
            <DeckTextDesktopRow
              text={text}
              selected={!narrow && text.id === selected?.id}
              onActivate={() => (narrow ? openText(text.id) : setSelectedId(text.id))}
            />
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div style={panelsShellStyle} data-testid="deck-texts-panels">
      <style>{ROW_CSS}</style>
      <div
        className={CONTROL_SURFACE}
        onContextMenu={suppressTouchContextMenu}
        style={narrow ? { ...leftColumnStyle, flex: '1 1 100%' } : leftColumnStyle}
      >
        <div style={{ flexShrink: 0, paddingRight: narrow ? 0 : 6, marginBottom: 12 }}>{head}</div>
        <div
          data-list-scroll
          style={{
            ...leftPanelStyle,
            paddingBottom: DECK_FLOOR_INSET,
            ...(narrow ? { borderRight: 'none', paddingRight: 0 } : null),
          }}
        >
          {list}
        </div>
      </div>

      {!narrow && (
        <div
          data-deck-pane
          data-testid="deck-text-pane"
          // The box is the pane's own scroller, as the card pane's is; its head sticks,
          // so the padding moves from the box to the head and the body.
          style={{ ...rightPanelStyle, width: PANE_WIDTH, padding: 0, paddingBottom: 0 }}
        >
          <TextPane
            id={selected?.id ?? null}
            onOpen={() => selected && openText(selected.id)}
            onPractice={() => selected && practiceText(selected.id)}
          />
        </div>
      )}
    </div>
  )
}

/** §19.6's row: radius 8, accent border and a 3 px leading bar when selected, no plane tint. */
function DeckTextDesktopRow({ text, selected, onActivate }: {
  text: TextListItem
  selected: boolean
  onActivate: () => void
}) {
  const { tr, tag } = useLocale()
  const gaps = text.gap_count > 0 ? tr.textList.gaps(text.gap_count) : tr.textList.noGaps
  const status = text.waiting_count > 0 ? tr.textList.waiting(text.waiting_count)
    : practiceStatus(text.practice, text.gap_count, tr, tag)
  const meta = [
    gaps,
    text.gap_count > 0 ? tr.deckTexts.level(text.level, TEXT_LEVEL_MAX) : null,
    status,
  ].filter(Boolean).join(' · ')

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      data-testid="deck-text-row"
      className="dt-drow"
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate() }
      }}
      style={{
        padding: '12px 14px', borderRadius: 8, background: 'var(--bg-surface)',
        border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
        // The leading bar sits inside the inset, so the row does not grow.
        boxShadow: selected ? 'inset 3px 0 0 var(--accent)' : 'none',
        display: 'flex', flexDirection: 'column', gap: 2,
      }}
    >
      <span style={{
        display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {text.title}
      </span>
      <span style={{
        display: 'block', fontFamily: 'var(--font-content)', fontSize: 13, lineHeight: 1.5,
        color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        <GappedText text={text.preview} spans={text.preview_gaps} blankWidth={40} blankTestId="deck-text-blank" />
      </span>
      <span data-testid="deck-text-meta" style={{ display: 'block', fontSize: 12, lineHeight: 1.4, color: 'var(--text-tertiary)' }}>
        {meta}
      </span>
    </div>
  )
}

/** The reading pane: eyebrow and «Открыть» in a sticky head, then the whole text. */
function TextPane({ id, onOpen, onPractice }: { id: string | null; onOpen: () => void; onPractice: () => void }) {
  const { tr } = useLocale()
  const t = tr.deckTexts
  const tp = tr.textPlayer
  const { data: text, isLoading, isError, refetch } = useText(id ?? undefined)

  if (id === null) {
    return (
      <p
        data-testid="deck-text-pane-empty"
        className={CONTROL_SURFACE}
        style={{ margin: 0, padding: 24, fontSize: 14, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}
      >
        {t.selectText}
      </p>
    )
  }
  return (
    // A column at least as tall as the pane, so the footer sits at its bottom even for a short text.
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div
        className={CONTROL_SURFACE}
        onContextMenu={suppressTouchContextMenu}
        style={{
          position: 'sticky', top: 0, zIndex: 1, height: 52, boxSizing: 'border-box', padding: '0 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)',
        }}
      >
        <span style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'var(--text-tertiary)', fontFamily: 'var(--font-ui)',
        }}>
          {t.paneEyebrow}
        </span>
        <OutlinePill onClick={onOpen}>{t.open}</OutlinePill>
      </div>
      {/* The text is content to read, and selectable. */}
      <div style={{ flex: 1, padding: `20px 24px ${DECK_FLOOR_INSET - 1}px` }}>
        {isLoading ? (
          <div aria-busy="true" style={{ height: 160, borderRadius: 8, background: 'var(--bg-secondary)', opacity: 0.6 }} />
        ) : isError || !text ? (
          <div role="alert" style={{ fontFamily: 'var(--font-ui)' }}>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--text-primary)' }}>{t.paneFailed}</p>
            <SecondaryPill onClick={() => refetch()}>{t.retry}</SecondaryPill>
          </div>
        ) : (
          <PaneText text={text} />
        )}
      </div>
      {/* Practice starts here (handoff §19.6): a text with no gaps has nothing to practise. */}
      {text && text.gaps.length > 0 && (
        <div
          data-testid="deck-text-pane-footer"
          className={CONTROL_SURFACE}
          onContextMenu={suppressTouchContextMenu}
          style={{
            position: 'sticky', bottom: 0, zIndex: 1, minHeight: 56, boxSizing: 'border-box', padding: '8px 24px',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            background: 'var(--bg-surface)', borderTop: '1px solid var(--border)', fontFamily: 'var(--font-ui)',
          }}
        >
          <SecondaryPill onClick={onPractice} data-testid="deck-text-practice">{tp.paneTrain}</SecondaryPill>
          <span data-testid="deck-text-practice-note" style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{tp.paneNote}</span>
        </div>
      )}
    </div>
  )
}

function PaneText({ text }: { text: TextDetail }) {
  const { tr, tag } = useLocale()
  const gaps = text.gaps.length > 0 ? tr.textList.gaps(text.gaps.length) : tr.textList.noGaps
  const status = text.waiting_words.length > 0 ? tr.textList.waiting(text.waiting_words.length)
    : practiceStatus(text.practice, text.gaps.length, tr, tag)
  const meta = [
    gaps,
    tr.deckTexts.words(text.word_count),
    text.gaps.length > 0 ? tr.deckTexts.level(text.level, TEXT_LEVEL_MAX) : null,
    status,
  ].filter(Boolean).join(' · ')

  return (
    <div data-testid="deck-text-pane-text">
      <h2 style={{
        margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3,
        color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', overflowWrap: 'anywhere',
      }}>
        {text.title}
      </h2>
      <p style={{ margin: '6px 0 16px', fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>{meta}</p>
      <p style={{
        margin: 0, fontFamily: 'var(--font-content)', fontSize: 16, lineHeight: '30px',
        color: 'var(--text-primary)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      }}>
        <GappedText
          text={text.body}
          spans={text.gaps.flatMap((g) => g.occurrences)}
          blankWidth={56}
          blankTestId="deck-pane-blank"
        />
      </p>
    </div>
  )
}
